import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../context/AuthContext";

const MAIN_COLOR = "#5a5248";

export default function Login() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirect = useMemo(
    () => searchParams.get("redirect") || "/",
    [searchParams],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    document.title = "Login - ERA AESTHETICS";
  }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      await signIn(email.trim(), password);
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(err?.message || "Unable to log in.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <Card>
        <Title>Log In</Title>
        <Subtitle>Welcome back.</Subtitle>

        {error ? <ErrorBox>{error}</ErrorBox> : null}

        <Form onSubmit={handleSubmit}>
          <Label>
            Email
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
          </Label>

          <Label>
            Password
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />
          </Label>

          <PrimaryButton type="submit" disabled={submitting}>
            {submitting ? "Logging in…" : "Log In"}
          </PrimaryButton>
        </Form>

        <FooterText>
          Don’t have an account? <Link to="/signup">Sign up</Link>
        </FooterText>

        {/* --- NEW LEGAL LINK --- */}
        <LegalFooter>
          Mit der Anmeldung erklären Sie sich mit unseren{" "}
          <Link to="/terms-and-conditions">Impressum & Datenschutz</Link>{" "}
          einverstanden.
        </LegalFooter>
      </Card>
    </Page>
  );
}

const LegalFooter = styled.p`
  margin-top: 24px;
  font-size: 0.75rem;
  opacity: 0.6;
  text-align: center;
  line-height: 1.4;
  border-top: 1px solid rgba(90, 82, 72, 0.1);
  padding-top: 16px;

  a {
    color: ${MAIN_COLOR};
    text-decoration: underline;
    text-underline-offset: 2px;
    font-weight: 500;
  }
`;

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 120px 20px 60px;
  background: #f9f8f6;
  color: ${MAIN_COLOR};
`;

const Card = styled.div`
  width: 100%;
  max-width: 520px;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(90, 82, 72, 0.12);
  border-radius: 24px;
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.06);
  padding: 34px;
`;

const Title = styled.h1`
  font-family: "The Seasons", serif;
  font-size: 2.4rem;
  font-weight: 400;
  margin: 0 0 8px;
  text-align: center;
`;

const Subtitle = styled.p`
  margin: 0 0 22px;
  opacity: 0.75;
  text-align: center;
`;

const Form = styled.form`
  display: grid;
  gap: 16px;
`;

const Label = styled.label`
  display: grid;
  gap: 8px;
  font-size: 0.9rem;
  opacity: 0.9;
`;

const Input = styled.input`
  border: 1px solid rgba(90, 82, 72, 0.18);
  border-radius: 16px;
  padding: 14px 16px;
  font-size: 1rem;
  outline: none;
  background: rgba(255, 255, 255, 0.9);

  &:focus {
    border-color: rgba(90, 82, 72, 0.45);
    box-shadow: 0 0 0 4px rgba(90, 82, 72, 0.08);
  }
`;

const PrimaryButton = styled.button`
  border: none;
  background: ${MAIN_COLOR};
  color: white;
  border-radius: 16px;
  padding: 14px 16px;
  cursor: pointer;
  font-size: 1rem;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorBox = styled.div`
  background: rgba(220, 38, 38, 0.08);
  border: 1px solid rgba(220, 38, 38, 0.2);
  color: #991b1b;
  border-radius: 16px;
  padding: 12px 14px;
  margin-bottom: 14px;
  font-size: 0.9rem;
`;

const FooterText = styled.p`
  margin: 18px 0 0;
  opacity: 0.8;
  text-align: center;
  a {
    color: ${MAIN_COLOR};
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 4px;
  }
`;
